import { toast } from 'sonner';

let deferredInstallPrompt: any = null;
const installListeners = new Set<(prompt: any) => void>();

/**
 * Enterprise PWA Controller.
 * Mounts standard Service Worker controllers and catches installation cues.
 */
export const pwaRegister = {
  /**
   * Safe registration script.
   */
  register(): void {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const isDevUrl = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' || 
                      window.location.hostname.includes('.run.app') || 
                      window.location.hostname.includes('aistudio') ||
                      window.location.hostname.includes('deepmind') ||
                      window.location.search.includes('disable-sw=true');

    if (isDevUrl) {
      console.log('[PWA] Dev mode or sandbox active. ServiceWorker skipped for HMR and loading stability.');
      
      // Clean up any existing Service Worker instances to prevent cache pollution
      try {
        navigator.serviceWorker.getRegistrations()
          .then(registrations => {
            for (let registration of registrations) {
              registration.unregister()
                .then(unregistered => {
                  if (unregistered) console.log('[PWA] Unregistered stale service worker successfully.');
                })
                .catch(() => {});
            }
          })
          .catch(() => {});
      } catch (e) {}

      // Clear standard caches
      try {
        if ('caches' in window) {
          caches.keys()
            .then(names => {
              for (let name of names) {
                caches.delete(name).catch(() => {});
              }
            })
            .catch(() => {});
        }
      } catch (err) {
        console.warn('[PWA] Failed to clear caches:', err);
      }
      return;
    }

    // Auto reload when service worker takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[PWA] ServiceWorker registered with scope:', registration.scope);

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Pre-caches loaded successfully, new code ready to run
                console.log('[PWA] New update discovered in background...');
                
                toast.info('Nova atualização disponível!', {
                  description: 'Clique aqui para recarregar e ativar novas funções instantaneamente.',
                  duration: 10000,
                  action: {
                    label: 'Atualizar',
                    onClick: () => {
                      newWorker.postMessage({ type: 'SKIP_WAITING' });
                    },
                  },
                });
              }
            });
          });
        })
        .catch((err) => {
          console.error('[PWA] ServiceWorker registration failure:', err);
        });
    });

    // Capture install prompt event for custom download designs
    window.addEventListener('beforeinstallprompt', (event: any) => {
      // Prevent browser default dialog box
      event.preventDefault();
      deferredInstallPrompt = event;
      
      // Notify active listeners
      installListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (err) {
          console.error('[PWA] Error alerting install listener:', err);
        }
      });
    });
  },

  /**
   * Expose current deferred prompt.
   */
  getInstallPrompt(): any {
    return deferredInstallPrompt;
  },

  /**
   * Let custom buttons easily trigger installation.
   */
  async promoteInstall(): Promise<boolean> {
    if (!deferredInstallPrompt) {
      return false;
    }

    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    console.log(`[PWA] Install choice outcome: ${outcome}`);
    
    deferredInstallPrompt = null;
    return outcome === 'accepted';
  },

  /**
   * Hook up install listeners.
   */
  onInstallPromptAvailable(callback: (prompt: any) => void): () => void {
    installListeners.add(callback);
    if (deferredInstallPrompt) {
      callback(deferredInstallPrompt);
    }
    return () => {
      installListeners.delete(callback);
    };
  },
};

export default pwaRegister;
